import { Link, useNavigate } from "react-router-dom";
import { GCP_REGIONS, isGcpProjectId } from "@mycompbox/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGcpBilling, useGcpPermissions, useGcpProjects } from "@/hooks/queries/use-cloud";
import { gcpWizardPath, useGcpWizard } from "./wizard-state";

const NO_BILLING = "none";

/** Step 3: a new project (recommended) or an existing one, plus billing account and region. */
export function ProjectStep() {
  const { state, update } = useGcpWizard();
  const navigate = useNavigate();
  const existing = state.projectMode === "existing";
  const projects = useGcpProjects(existing ? state.session : null);
  const checkedProject = existing && isGcpProjectId(state.projectId) ? state.projectId : undefined;
  const billing = useGcpBilling(state.session, checkedProject);
  const permissions = useGcpPermissions(state.session, checkedProject);

  if (!state.session) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Sign in with Google first, then choose a project.</p>
        <Button asChild>
          <Link to={gcpWizardPath("sign-in")}>Sign in with Google</Link>
        </Button>
      </div>
    );
  }

  const idOk = isGcpProjectId(state.projectId.trim());
  const missing = permissions.data?.missing ?? [];
  const roleBlocked = permissions.data ? !permissions.data.ok : false;
  const canContinue = idOk && !!state.region && !roleBlocked;

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold">Pick a project and billing</h2>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-medium">Project</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="gcp-project-mode"
            checked={!existing}
            onChange={() => update({ projectMode: "new", projectId: "" })}
          />
          Create a new project (recommended)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="gcp-project-mode"
            checked={existing}
            onChange={() => update({ projectMode: "existing", projectId: "" })}
          />
          Use an existing project
        </label>
      </fieldset>

      {existing ? (
        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="gcp-existing-project">Existing project</Label>
          {projects.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Couldn't list your Google Cloud projects. Sign in again.
            </p>
          ) : (
            <Select value={state.projectId} onValueChange={(projectId) => update({ projectId })}>
              <SelectTrigger id="gcp-existing-project">
                <SelectValue placeholder={projects.isLoading ? "Loading projects…" : "Choose a project"} />
              </SelectTrigger>
              <SelectContent>
                {(projects.data?.projects ?? []).map((p) => (
                  <SelectItem key={p.projectId} value={p.projectId} disabled={!p.open}>
                    {p.name} ({p.projectId}, …{p.maskedProjectNumber})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : (
        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="gcp-new-project">New project ID</Label>
          <Input
            id="gcp-new-project"
            value={state.projectId}
            placeholder="acme-compbox"
            onChange={(e) => update({ projectId: e.target.value.trim() })}
          />
          {state.projectId !== "" && !idOk && (
            <p className="text-xs text-destructive">Use 6–30 lowercase letters, numbers and dashes, starting with a letter.</p>
          )}
        </div>
      )}

      <div className="max-w-sm space-y-1.5">
        <Label htmlFor="gcp-billing">Billing account</Label>
        {billing.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Couldn't list your billing accounts.
          </p>
        ) : (
          <Select
            value={state.billingAccountId === "" ? NO_BILLING : state.billingAccountId}
            onValueChange={(v) => update({ billingAccountId: v === NO_BILLING ? "" : v })}
          >
            <SelectTrigger id="gcp-billing">
              <SelectValue placeholder={billing.isLoading ? "Loading billing accounts…" : "Choose a billing account"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_BILLING}>No billing account (no budget is created)</SelectItem>
              {(billing.data?.accounts ?? []).map((a) => (
                <SelectItem key={a.id} value={a.id} disabled={!a.open}>
                  {a.displayName} ({a.maskedId})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {billing.data?.projectBillingEnabled === false && (
          <p className="text-xs text-muted-foreground">This project has no billing yet; the billing account you pick is linked to it.</p>
        )}
      </div>

      <div className="max-w-sm space-y-1.5">
        <Label htmlFor="gcp-region">Region</Label>
        <Select value={state.region} onValueChange={(region) => update({ region })}>
          <SelectTrigger id="gcp-region">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GCP_REGIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {missing.length > 0 && (
        <div role="alert" className="space-y-2 rounded-md border border-destructive/40 p-3 text-sm">
          <p className="font-medium text-destructive">Your Google account is missing a role CompBox needs.</p>
          {missing.map((m) => (
            <div key={m.role} className="space-y-1">
              <p>
                {m.label} <code className="text-xs">({m.role})</code>: {m.message}
              </p>
              <Label htmlFor={`request-${m.role}`} className="text-xs text-muted-foreground">
                Send this to the owner of the Google account
              </Label>
              <textarea
                id={`request-${m.role}`}
                readOnly
                value={m.requestForOwner}
                rows={3}
                className="w-full rounded-md border bg-transparent p-2 text-xs"
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Button disabled={!canContinue} onClick={() => navigate(gcpWizardPath("plan"))}>
          Continue
        </Button>
        <Button asChild variant="ghost">
          <Link to={gcpWizardPath("sign-in")}>Back</Link>
        </Button>
      </div>
    </div>
  );
}
